import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FIND_FUNERAL_SERVICES } from '~graphql/queries/funeralServices';
import getGraphQlClient from '~graphql/getGraphQlClient';
import { unmarshalGraphqlResponse } from '~graphql/utils';
import { onErrorShowMessage } from '~src/errors';
import { useTable, usePagination } from 'react-table';
import BTable from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import InputGroup from 'react-bootstrap/InputGroup';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import format from 'date-fns/format';
import formatDistance from 'date-fns/formatDistance';
import it from 'date-fns/locale/it';

function prepareStringForIndexing(str = '') {
  return str
    .normalize()
    .toLowerCase()
    .replace(/[^\w\s]/gi, ' ')
    .trim();
}

/* eslint-disable react/prop-types */
function RenderDataMorte({ value }) {
  let output = value;
  try {
    output = format(new Date(value), 'dd/MM/yyyy');
  } catch (err) {
    console.error(err);
  }
  return <span>{output}</span>;
}
function RenderCreatedAt({ value }) {
  let output = value;
  try {
    output = formatDistance(new Date(value), new Date(), {
      addSuffix: false,
      locale: it,
    });
  } catch (err) {
    console.error(err);
  }
  return <span>{output}</span>;
}
function RenderSesso({ value }) {
  if (!value) {
    return <span></span>;
  }
  if (value.toLowerCase() === 'm') {
    return <span>Uomo</span>;
  } else if (value.toLowerCase() === 'f') {
    return <span>Donna</span>;
  }
}
/* eslint-enable react/prop-types */

const FuneralServicesSelector = (props) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageInfo, setPageInfo] = useState();
  const [count, setCount] = useState(0);
  const [controlledPageCount, setControlledPageCount] = useState(0);
  const columns = React.useMemo(
    () => [
      {
        Header: 'Cognome',
        accessor: 'cognome',
      },
      {
        Header: 'Nome',
        accessor: 'nome',
      },
      {
        Header: 'Sesso',
        accessor: 'sesso',
        Cell: RenderSesso,
      },
      {
        Header: 'Data Morte',
        accessor: 'data_morte',
        Cell: RenderDataMorte,
      },
      {
        Header: 'Creato',
        accessor: 'createdAt',
        Cell: RenderCreatedAt,
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    // Get the state from the instance
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: items,
      initialState: { pageIndex: 0 }, // Pass our hoisted table state
      manualPagination: true, // Tell the usePagination
      // hook that we'll handle our own data fetching
      // This means we'll also have to provide our own
      // pageCount.
      pageCount: controlledPageCount,
    },
    usePagination
  );

  const fetchData = async (forceFetch = false, _searchTerm = '') => {
    try {
      const graphQlClient = await getGraphQlClient();

      const variables = {
        limit: pageSize,
        where: {},
      };

      if (pageIndex) {
        variables.after = pageInfo.endCursor;
      }

      if (_searchTerm) {
        variables.where.searchByName = {
          in: [prepareStringForIndexing(searchTerm)],
        };
      }

      const response = await graphQlClient.query({
        fetchPolicy: forceFetch ? 'network-only' : 'cache-first',
        variables,
        query: FIND_FUNERAL_SERVICES,
      });

      return unmarshalGraphqlResponse(response.data, 'defuntos');
    } catch (error) {
      onErrorShowMessage();
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData(false, searchTerm)
      .then((response) => {
        setItems(response.edges);
        setPageInfo(response.pageInfo);
        setCount(response.count);
        setControlledPageCount(Math.ceil(response.count / pageSize));
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        onErrorShowMessage();
      });
  }, [pageSize, pageIndex, searchTerm]);

  const handleOnChange = (record) => {
    if (props.onChange) {
      props.onChange(record);
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col xs={12} md={6}>
          {' '}
          <InputGroup className="mb-3">
            <InputGroup.Text>Cerca per nome</InputGroup.Text>
            <Form.Control
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
            />
          </InputGroup>
        </Col>

        <Col md={5}>
          <span className="pr-3">
            Pagina{' '}
            <strong>
              {pageIndex + 1} di {pageOptions.length}
            </strong>{' '}
          </span>{' '}
          <Button
            className="mr-1"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
          >
            {'<<'}
          </Button>
          <Button
            className="mr-2"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            {'<'}
          </Button>
          <Button
            className="mr-1"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            {'>'}
          </Button>
          <Button
            className="mr-1"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
          >
            {'>>'}
          </Button>
        </Col>
        <Col md={1}>
          <Form.Control
            as="select"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
            }}
          >
            {[10, 25].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </Form.Control>
        </Col>
      </Row>
      <Row>
        <Col>
          <BTable striped bordered hover size="sm" {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup, i) => (
                <tr
                  key={`row-head-${i}`}
                  {...headerGroup.getHeaderGroupProps()}
                >
                  {headerGroup.headers.map((column, j) => (
                    <th key={`row-head-${i}-${j}`} {...column.getHeaderProps()}>
                      {column.render('Header')}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr
                    key={`row-body-${i}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleOnChange(row.original)}
                    {...row.getRowProps()}
                  >
                    {row.cells.map((cell, i) => {
                      return (
                        <td key={`td-${i}`} {...cell.getCellProps()}>
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              <tr>
                {loading ? (
                  <td colSpan="10000">Caricamento...</td>
                ) : (
                  <td colSpan="10000">
                    Stai visualizzando {page.length} dei {count} servizi
                    presenti
                  </td>
                )}
              </tr>
            </tbody>
          </BTable>
        </Col>
      </Row>
    </Container>
  );
};

FuneralServicesSelector.propTypes = {
  onChange: PropTypes.func,
};

export default FuneralServicesSelector;
